import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreHotelsComponent } from "@b2bportal/core-types";
import { HotelSteps, NavigationFlow } from "@b2bportal/core-utilities";
import {
  AboutHotelSection,
  Amenities,
  CustomerReviewsSection,
  CheckInPoliciesSection,
  CheckInInformationSection,
  HotelInfoSection,
  ImageGallery,
  HotelRoomSelectionSection,
} from "@tenant/hotels";
import { ErrorMessage, Loading, Page } from "@tenant/ui";
import clsx from "clsx";
import { useHotelSlot } from "../../../../slots";
import {
  HotelsRawEventName,
  useEmitCoreHotelsRawEventOnce,
} from "../../../../tracking";

import defaultStyles from "./ShopPage.module.scss";

import type { ShopPageProps } from "./types";

const COMPONENT_KEY = CoreHotelsComponent.ShopPage;
export const ShopPage = ({
  className,
  children,
  context,
  text,
  handlers,
  ...pageProps
}: ShopPageProps) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const HotelSlot = useHotelSlot();

  const currentStep =
    NavigationFlow.useGetCurrentStep<HotelSteps>() ?? HotelSteps.SHOP;

  useEmitCoreHotelsRawEventOnce(
    {
      name: HotelsRawEventName.HotelShopPageLoaded,
    },
    !context.isLoading && context.hotel != null
  );

  if (context.isUnavailable || context.isError) {
    return (
      <Page className={clsx(block, className, cn("page-error"))}>
        <div className={cn("error")}>
          <ErrorMessage
            title={text.unavailableTitle}
            subtitle={text.unavailableSubtitle}
          />
        </div>
      </Page>
    );
  }

  //TODO: skeleton loading on the components
  if (context.isLoading || context.hotel == null) {
    return (
      <Page
        title={text.title}
        className={clsx(block, className, cn("page-loading"))}
      >
        <div className={cn("loading")}>
          <Loading />
        </div>
      </Page>
    );
  }

  return (
    <Page title={text.title} className={clsx(block, className)} {...pageProps}>
      <div className={cn("container")}>
        <div className={cn("content")}>
          <HotelSlot.HotelBreadcrumbs
            className={cn("breadcrumbs")}
            currentStep={currentStep}
          />
          <ImageGallery
            className={cn("image-gallery")}
            media={context.hotel.lodging.media ?? []}
            name={context.hotel.lodging.name}
          />
          <HotelSlot.HotelShopHeader className={cn("shop-header")} />
          <HotelInfoSection
            className={cn("section", ["main", "info"])}
            hotelData={context.hotel.lodging}
          />
          <Amenities
            className={cn("section", ["main"])}
            amenities={context.amenities}
          />
          <AboutHotelSection
            className={cn("section", ["main"])}
            hotel={context.hotel}
          />
          {context.reviews != null ? (
            <CustomerReviewsSection
              className={cn("section", ["main"])}
              reviews={context.reviews}
            />
          ) : null}
          <CheckInPoliciesSection
            className={cn("section", ["main"])}
            checkInPolicies={context.checkInPolicies}
          />
          <CheckInInformationSection
            className={cn("section", ["main", "check-in-information"])}
            checkInInformation={context.checkInInformation}
          />

          <HotelSlot.HotelShopBookingSection
            className={cn("section", ["secondary", "price-section"])}
            hotel={context.hotel}
            goToNextStep={handlers.handleChooseRooms}
          />

          {context.isDesktopAndUp ? (
            <HotelRoomSelectionSection
              title={text.roomSelectionTitle}
              ref={context.roomSectionRef}
              goToNextStep={handlers.goToNextStep}
              className={cn("room-selection")}
            />
          ) : null}
          {children}
        </div>
      </div>
    </Page>
  );
};
